import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';

interface DealSubmissionFormProps {
  selectedPlan: 'standard' | 'featured';
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

export function DealSubmissionForm({ selectedPlan }: DealSubmissionFormProps) {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    category: '',
    dealTitle: '',
    dealDescription: '',
    discountValue: '',
    discountType: 'percentage',
    terms: '',
    startDate: '',
    endDate: '',
    maxRedemptions: '',
    pricingModel: 'flat',
    agreeToTerms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const categories = [
    'Food & Dining',
    'Retail & Shopping',
    'Health & Wellness',
    'Beauty & Spa',
    'Automotive',
    'Home Services',
    'Entertainment',
    'Fitness',
    'Professional Services',
    'Other'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const generateCouponCode = () => {
    const businessPrefix = formData.businessName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 3);
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${businessPrefix}${randomNum}`;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.dealTitle.trim()) newErrors.dealTitle = 'Deal title is required';
    if (!formData.dealDescription.trim()) newErrors.dealDescription = 'Deal description is required';
    if (!formData.discountValue.trim()) newErrors.discountValue = 'Discount value is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const couponCode = generateCouponCode();
      const planPrice = selectedPlan === 'standard' ? 2500 : 10000;

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-deal-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          dealData: {
            ...formData,
            couponCode,
            plan: selectedPlan
          },
          amount: planPrice,
          planType: selectedPlan
        })
      });

      const { sessionId } = await response.json();
      const stripe = await stripePromise;
      
      if (stripe) {
        await stripe.redirectToCheckout({ sessionId });
      }
    } catch (error) {
      console.error('Error submitting deal:', error);
      alert('There was an error processing your submission. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const planDetails = {
    standard: { name: 'Standard Deal', price: '$25-50/month' },
    featured: { name: 'Featured Deal', price: '$100-200/month' }
  };

  return (
    <section id="deal-submission-form" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-8 py-6">
            <h2 className="text-3xl font-bold text-white mb-2">Submit Your Deal</h2>
            <p className="text-emerald-50">
              Selected Plan: <strong>{planDetails[selectedPlan].name}</strong> ({planDetails[selectedPlan].price})
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Business Information */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-store-2-line text-emerald-600"></i>
                Business Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Your Business Name"
                  />
                  {errors.businessName && <p className="text-red-600 text-sm mt-1">{errors.businessName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {errors.category && <p className="text-red-600 text-sm mt-1">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                  {errors.contactName && <p className="text-red-600 text-sm mt-1">{errors.contactName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="john@business.com"
                  />
                  {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="(916) 555-0123"
                  />
                  {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="https://yourbusiness.com"
                  />
                </div>
              </div>
            </div>

            {/* Deal Details */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-emerald-600"></i>
                Deal Details
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Deal Title *
                  </label>
                  <input
                    type="text"
                    name="dealTitle"
                    value={formData.dealTitle}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="e.g., 50% Off First Visit"
                    maxLength={100}
                  />
                  {errors.dealTitle && <p className="text-red-600 text-sm mt-1">{errors.dealTitle}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Deal Description *
                  </label>
                  <textarea
                    name="dealDescription"
                    value={formData.dealDescription}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                    placeholder="Describe your deal in detail. What's included? Any restrictions?"
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.dealDescription && <p className="text-red-600 text-sm">{errors.dealDescription}</p>}
                    <p className="text-sm text-gray-500 ml-auto">{formData.dealDescription.length}/500</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Discount Value *
                    </label>
                    <input
                      type="text"
                      name="discountValue"
                      value={formData.discountValue}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="e.g., 50 or 20"
                    />
                    {errors.discountValue && <p className="text-red-600 text-sm mt-1">{errors.discountValue}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Discount Type
                    </label>
                    <select
                      name="discountType"
                      value={formData.discountType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="dollar">Dollar Amount ($)</option>
                      <option value="bogo">Buy One Get One</option>
                      <option value="free">Free Item/Service</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Terms & Conditions
                  </label>
                  <textarea
                    name="terms"
                    value={formData.terms}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                    placeholder="e.g., Valid for new customers only. Cannot be combined with other offers."
                    maxLength={300}
                  />
                  <p className="text-sm text-gray-500 mt-1">{formData.terms.length}/300</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    {errors.startDate && <p className="text-red-600 text-sm mt-1">{errors.startDate}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    {errors.endDate && <p className="text-red-600 text-sm mt-1">{errors.endDate}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Max Redemptions
                    </label>
                    <input
                      type="number"
                      name="maxRedemptions"
                      value={formData.maxRedemptions}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Unlimited"
                      min="1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Model */}
            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <i className="ri-money-dollar-circle-line text-emerald-600"></i>
                Pricing Model
              </h3>
              <div className="space-y-4">
                <label className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors">
                  <input
                    type="radio"
                    name="pricingModel"
                    value="flat"
                    checked={formData.pricingModel === 'flat'}
                    onChange={handleChange}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-bold text-gray-900">Flat Monthly Rate</div>
                    <div className="text-sm text-gray-600">
                      {selectedPlan === 'standard' ? '$25-50/month' : '$100-200/month'} - Unlimited redemptions
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors">
                  <input
                    type="radio"
                    name="pricingModel"
                    value="commission"
                    checked={formData.pricingModel === 'commission'}
                    onChange={handleChange}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-bold text-gray-900">Commission-Based</div>
                    <div className="text-sm text-gray-600">
                      10-15% per redemption - Only pay when customers use your deal
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Terms Agreement */}
            <div className="pt-6 border-t border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  I agree to the <a href="/terms" className="text-emerald-600 hover:underline">Terms of Service</a> and understand that my deal will be reviewed before going live. I authorize Roseville.com to display my business information and deal details on the platform.
                </span>
              </label>
              {errors.agreeToTerms && <p className="text-red-600 text-sm mt-2">{errors.agreeToTerms}</p>}
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-check-line"></i>
                    Submit Deal & Proceed to Payment
                  </span>
                )}
              </button>
              <p className="text-center text-sm text-gray-600 mt-4">
                Your unique coupon code will be generated after submission
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}